import { useState, type JSX } from "react";
import type { NextStepsBlock as NextStepsBlockModel, Suggestion } from "@shared/types";
import type { BlockRendererProps } from "./registry";
import { dismissNextStep } from "../../../lib/api";

/**
 * Suggested-next-steps renderer (openspec/suggested-next-steps.md): 1..N
 * one-click chips after a settled answer. Clicking a chip sends its embedded
 * prompt as the next user message (the FreeformButton funnel); the ✕ dismisses
 * it — hidden immediately, persisted fire-and-forget so it stays hidden on
 * reload and the generator stops re-proposing it. Only `prompt` actions are
 * generated this phase; other action types are skipped defensively so a newer
 * server degrades gracefully on this client. An emptied block renders nothing.
 */
export function NextStepsBlockView({
  block,
  onSendPrompt,
  onSuggestionAction,
  conversationId
}: BlockRendererProps): JSX.Element {
  const ns = block as NextStepsBlockModel;
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  // Show prompt chips always; connect/upload chips only when a handler is wired
  // (P4c) so an older host without it degrades to prompt-only gracefully.
  const visible = ns.suggestions.filter(
    (s) =>
      !dismissed.has(s.id) &&
      (s.action.type === "prompt" || onSuggestionAction !== undefined)
  );
  if (visible.length === 0) return <></>;

  const dismiss = (s: Suggestion): void => {
    setDismissed((prev) => new Set([...prev, s.id]));
    if (conversationId !== undefined) {
      void dismissNextStep(conversationId, s.id, s.label);
    }
  };

  return (
    <div className="next-steps-block" data-testid="next-steps-block">
      <div className="next-steps-label">建议的下一步</div>
      {visible.map((s) => (
        <div key={s.id} className="next-steps-chip" data-testid="next-steps-chip">
          <button
            type="button"
            className="next-steps-chip-main"
            title={s.reason ?? ""}
            onClick={() => {
              if (s.action.type === "prompt") onSendPrompt(s.action.prompt);
              else onSuggestionAction?.(s.action);
            }}
          >
            <span className="next-steps-chip-text">{s.label}</span>
            {s.hint !== undefined && s.hint.length > 0 && (
              <span className="next-steps-chip-hint">{s.hint}</span>
            )}
          </button>
          <button
            type="button"
            className="next-steps-dismiss"
            aria-label="忽略这条建议"
            onClick={() => dismiss(s)}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
